export type Language = "en" | "es";

export const STRINGS = {
  en: {
    appName: "Pulso Reach",
    tagline: "Compliance-first bilingual messaging",
    nav: {
      dashboard: "Dashboard",
      campaigns: "Campaigns",
      flows: "Flows",
      contacts: "Contacts",
      consent: "Consent ledger",
      reports: "Reports",
      send: "Manual send",
      settings: "Settings",
    },
    common: {
      en: "English",
      es: "Spanish",
      save: "Save",
      cancel: "Cancel",
      create: "Create",
      delete: "Delete",
      edit: "Edit",
      yes: "Yes",
      no: "No",
      back: "Back",
      next: "Next",
      preview: "Preview",
      review: "Review",
      send: "Send",
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      pending: "Pending",
      total: "Total",
      language: "Language",
    },
    dashboard: {
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      optedOut: "Opted out",
      responseRate: "Response rate",
      completionRate: "Completion rate",
      byLanguage: "By language",
      sentiment: "Sentiment / category",
      live: "Live",
    },
    campaign: {
      new: "New campaign",
      type: "Campaign type",
      types: { political: "Political", nonprofit: "Nonprofit", employer: "Employer", commercial: "Commercial" },
      channel: "Channel",
      channels: { sms: "SMS", whatsapp: "WhatsApp" },
      bilingualNote: "All templates and disclosures are stored per language. A contact's language preference selects which is sent.",
      registration: "Carrier / platform registration",
      registered: "Registered & approved",
      notRegistered: "Not registered",
      cta: "Opt-in CTA preview",
      helpReply: "HELP reply preview",
      stopReply: "STOP reply preview",
    },
    contacts: {
      import: "Import contacts",
      count: "Contacts",
      suppressed: "Suppressed",
      addManually: "Add manually",
      phone: "Phone (E.164)",
      firstName: "First name",
      consent: "Consent",
      source: "Source",
      timestamp: "Timestamp",
      disclosureVersion: "Disclosure version",
    },
    send: {
      title: "Manual send",
      help: "Prewrite a message; the system gates the send through compliance checks (consent, quiet hours, suppression, sender eligibility).",
      pickCampaign: "Choose a campaign",
      pickContact: "Choose a contact",
      body: "Message body",
      reviewSend: "Review & send",
      checks: "Compliance checks",
      pass: "Pass",
      block: "Blocked",
    },
    reports: {
      export: "Export report",
      csv: "CSV",
      pdf: "PDF (print)",
      methodology: "Methodology & compliance appendix",
    },
    compliance: {
      title: "Compliance & consent",
      stopFamily: "STOP / opt-out keywords",
      helpFamily: "HELP keywords",
      quietHours: "Quiet hours",
      disclosureLibrary: "Disclosure library",
    },
    marketing: {
      nav: {
        about: "About",
        polls: "Poll types",
        method: "Methodology",
        research: "Research",
        contact: "Contact",
        launchDemo: "Platform tour",
      },
      hero: {
        eyebrow: "Multilingual research firm",
        title: "Multilingual research the way your audience actually responds.",
        subtitle: "Pulso Reach is a multilingual SMS and WhatsApp research platform built around how communities actually use language, identity, and trust. Deep specialization in Spanish and English (where we built the playbook); growing capability in Vietnamese, Korean, Haitian Creole, Arabic, and Mandarin. Consent, opt-out, and quiet-hour compliance are enforced on every send.",
        ctaPrimary: "Take the platform tour",
        ctaSecondary: "See poll types",
        demoNote: "Pulso Reach is an active multilingual research platform; the interactive tour runs entirely in your browser, so nothing you click leaves your device.",
      },
      stats: {
        title: "Why multilingual communities need a different platform",
        items: [
          { value: "~62.5M", label: "U.S. Hispanic population (roughly 19% of the country and the largest minority group); the proving ground for our methodology" },
          { value: "~67M", label: "U.S. residents who speak a language other than English at home: Spanish, Chinese, Tagalog, Vietnamese, Arabic, Korean, French Creole, and more" },
          { value: "~43%", label: "of U.S. Hispanics who select 'some other race' on the Census; a sign of how standard instruments flatten identity for many communities" },
          { value: "~3 in 4", label: "U.S. Latinos who say they have never heard of, or do not use, the term 'Latinx'; default terminology in standard polling routinely misses how people actually self-identify" },
        ],
      },
      whyDifferent: {
        title: "Multilingual polling is not English polling translated.",
        body: "Decades of survey methodology research show that multilingual U.S. communities are not one audience; they are layered sets of national-origin groups, generations, language profiles, and racial self-concepts. A generic translated survey, sent at the wrong hour, in the wrong dialect, through a channel the respondent doesn't trust, will systematically miss the people whose voice you most need to hear. Latino isn't a side project for us; it's where we built the playbook. That playbook now serves Asian American, MENA, Caribbean, and African immigrant communities with the same discipline.",
      },
      pollsHeading: "Polls we can run",
      pollsSub: "Every poll type below runs through the same compliance engine (STOP / HELP, quiet hours, consent ledger, suppression) and supports branching logic, mixed open / closed questions, and parallel instruments across languages (bilingual English / Spanish standard; additional languages on request).",
      polls: [
        { title: "Voter intent & ballot tracking", body: "Likely-voter screens, candidate preference, vote method (mail / early / day-of), and turnout intent; fielded weekly or as a rolling tracker." },
        { title: "Issue salience & policy testing", body: "Top-of-mind issue batteries, forced-choice trade-offs, and policy framing tests with country-of-origin, language, and generation crosstabs." },
        { title: "Persuasion & message testing", body: "Randomized message arms, pre / post movement scores, and open-ended reaction capture in the respondent's preferred language." },
        { title: "GOTV & turnout pulses", body: "Short, repeatable nudges with response-rate and self-reported turnout tracking, gated by quiet hours and per-state political messaging rules." },
        { title: "Civic engagement & trust", body: "Trust in institutions, perceived discrimination, civic participation, and information-source surveys; designed to be safe for respondents to answer honestly in their own language." },
        { title: "Employer & frontline workforce pulse", body: "Multilingual pulse surveys for hourly, frontline, and language-minority workforces where email surveys underperform." },
        { title: "Customer experience & NPS", body: "Post-interaction NPS / CSAT for brands serving multilingual consumers, with item-level translation review and response-style controls." },
        { title: "Education & parent voice", body: "Parent-engagement, school-climate, and language-access surveys for districts and education nonprofits." },
        { title: "Brand & ad recall", body: "Unaided / aided recall, sentiment, and creative testing with language-dominant, English-dominant, and bilingual cuts." },
      ],
      method: {
        title: "How we run multilingual research (built on our Latino playbook)",
        sub: "Eight methodology choices, drawn from the literature on Latino survey response (where we built the discipline), now applied across every language community we serve.",
        items: [
          { title: "Targeted multilingual, not blanket multilingual", body: "Recent split-mode experiments find that broadly sending bilingual invitations can slightly lower overall response, while targeted multilingual outreach to likely non-English-dominant households measurably increases reach into those communities. Pulso Reach routes language by contact preference and surname / geography signals; never a blanket toggle." },
          { title: "Multi-dimensional identity capture", body: "We ask country of origin, pan-ethnic identity, racial self-concept, generation, and language dominance as separate fields. The U.S. Census race question alone misses how most Latinos (and many Asian, Arab, and Caribbean respondents) describe themselves." },
          { title: "No 'Latinx' (or any imposed label) by default", body: "Public-opinion research consistently finds that roughly three-quarters of U.S. Latinos have never heard of or do not use 'Latinx'. Our default instruments use the terms each community actually uses; community-imposed labels appear only when respondents self-select them." },
          { title: "BISG + GIS-based sampling", body: "For outreach lists we combine Bayesian Improved Surname Geocoding with census-tract-level density (Hispanic, AAPI, MENA, Black African / Caribbean) to stratify and weight; improving reach into language-dominant and mixed-status neighborhoods." },
          { title: "Cognitive interviewing on every translation", body: "Translated items are cognitively tested with bilingual respondents before fielding. Even high-quality translations show item-bias drift; pretesting catches it. This is non-negotiable in any language we field." },
          { title: "Mixed-mode by design", body: "SMS-first, with WhatsApp, mail, phone, and in-person fallbacks for hard-to-reach segments. Mode is a covariate, not a constant." },
          { title: "Respondent-driven sampling for hidden populations", body: "For undocumented, mixed-status, and other hidden populations we use respondent-driven sampling chains with stricter consent and stronger data-minimization rules." },
          { title: "Cultural protocol: warm, person-centered recruitment", body: "Recruitment scripts, opening messages, and qualitative follow-ups are designed around community-appropriate norms (personalismo, simpatía, pláticas in Latino fielding; equivalent norms in other communities); never transactional survey patter." },
        ],
      },
      research: {
        title: "Grounded in the published research",
        sub: "The methodology above is drawn from peer-reviewed and working-paper literature on Latino survey response (where the discipline is most developed), and applied across language communities we serve. Selected sources:",
        items: [
          { cite: "Antman, F. M. (2021). Multi-Dimensional Identities of the Hispanic Population in the United States. IZA DP No. 14815.", url: "https://www.iza.org/publications/dp/14815" },
          { cite: "Wagner, J. et al. (2023). Effects of bilingual survey invitations on response in a general-population mail survey.", url: "https://academic.oup.com/jssam" },
          { cite: "Olson, K. et al. (2025). Targeted vs. blanket bilingual outreach in mixed-mode surveys.", url: "https://academic.oup.com/poq" },
          { cite: "Noe-Bustamante, L., Mora, L., & Lopez, M. H. (2020). About one-in-four U.S. Hispanics have heard of Latinx, but just 3% use it. Pew Research Center.", url: "https://www.pewresearch.org/race-and-ethnicity/2020/08/11/about-one-in-four-u-s-hispanics-have-heard-of-latinx-but-just-3-use-it/" },
          { cite: "Barreto, M. A., et al. (2018). Best practices in surveying hard-to-reach Latino populations.", url: "https://www.latinodecisions.com" },
          { cite: "Elliott, M. N., et al. Bayesian Improved Surname Geocoding (BISG) methodology. RAND.", url: "https://www.rand.org/health-care/tools-methods/bisg.html" },
        ],
        disclaimer: "Pulso Reach is an independent research firm; the sources above inform our methodology but are not affiliated with us.",
      },
      demoBlock: {
        title: "See the platform in action",
        body: "The interactive tour includes the compliance dashboard, campaign builder, branching survey flows, consent ledger, contact import, and CSV / printable reports (all bilingual English / Spanish; additional languages configurable per project).",
        cta: "Open the platform tour",
        features: [
          "STOP / HELP / START enforcement in English and Spanish",
          "Quiet-hour blocking by contact timezone",
          "Versioned disclosures and full consent ledger",
          "Branching survey flow editor with sample voter-intent flow",
          "Live KPI dashboard with language and sentiment cuts",
          "CSV and print-to-PDF reports with methodology appendix",
        ],
      },
      contact: {
        title: "Talk to us about a study",
        body: "Pulso Reach is actively taking new study engagements. Tell us about your audience, the questions you need answered, and your timeline; we will follow up within two business days.",
        formIntro: "All fields optional except name and email; we will only use what you share to scope a response.",
        fields: {
          name: "Name",
          email: "Email",
          org: "Organization",
          topic: "What you would like to discuss",
          message: "A few sentences about your study or question",
          submit: "Send message",
          sending: "Sending\u2026",
          success: "Thank you. Your message is in; we will be in touch within two business days.",
          error: "Something went wrong sending that. Please email contact@pulsoreach.org directly or try again.",
        },
        topics: [
          "Scoping a new study",
          "Methodology question",
          "Data or sample request",
          "Press or interview",
          "Partnership",
          "Other",
        ],
        githubFallback: "Prefer GitHub? View the project repository.",
        url: "https://github.com/Mdr-palacios/mensajeando",
      },
      footer: {
        rights: "Active multilingual research platform. Built compliance-first.",
        sourceCode: "Source code",
      },
    },
  },
  es: {
    appName: "Pulso Reach",
    tagline: "Mensajería bilingüe con cumplimiento primero",
    nav: {
      dashboard: "Panel",
      campaigns: "Campañas",
      flows: "Flujos",
      contacts: "Contactos",
      consent: "Registro de consentimiento",
      reports: "Reportes",
      send: "Envío manual",
      settings: "Configuración",
    },
    common: {
      en: "Inglés",
      es: "Español",
      save: "Guardar",
      cancel: "Cancelar",
      create: "Crear",
      delete: "Eliminar",
      edit: "Editar",
      yes: "Sí",
      no: "No",
      back: "Atrás",
      next: "Siguiente",
      preview: "Vista previa",
      review: "Revisar",
      send: "Enviar",
      sent: "Enviado",
      delivered: "Entregado",
      failed: "Fallido",
      pending: "Pendiente",
      total: "Total",
      language: "Idioma",
    },
    dashboard: {
      sent: "Enviados",
      delivered: "Entregados",
      failed: "Fallidos",
      optedOut: "Cancelados",
      responseRate: "Tasa de respuesta",
      completionRate: "Tasa de finalización",
      byLanguage: "Por idioma",
      sentiment: "Sentimiento / categoría",
      live: "En vivo",
    },
    campaign: {
      new: "Nueva campaña",
      type: "Tipo de campaña",
      types: { political: "Política", nonprofit: "Sin fines de lucro", employer: "Empleador", commercial: "Comercial" },
      channel: "Canal",
      channels: { sms: "SMS", whatsapp: "WhatsApp" },
      bilingualNote: "Todas las plantillas y avisos se guardan por idioma. La preferencia del contacto determina cuál se envía.",
      registration: "Registro con operador / plataforma",
      registered: "Registrado y aprobado",
      notRegistered: "No registrado",
      cta: "Vista previa de llamada a la acción",
      helpReply: "Vista previa de respuesta HELP",
      stopReply: "Vista previa de respuesta STOP",
    },
    contacts: {
      import: "Importar contactos",
      count: "Contactos",
      suppressed: "Suprimidos",
      addManually: "Agregar manualmente",
      phone: "Teléfono (E.164)",
      firstName: "Nombre",
      consent: "Consentimiento",
      source: "Fuente",
      timestamp: "Fecha y hora",
      disclosureVersion: "Versión del aviso",
    },
    send: {
      title: "Envío manual",
      help: "Redacte previamente un mensaje; el sistema controla el envío mediante verificaciones de cumplimiento (consentimiento, horario, supresión, elegibilidad del remitente).",
      pickCampaign: "Elegir campaña",
      pickContact: "Elegir contacto",
      body: "Texto del mensaje",
      reviewSend: "Revisar y enviar",
      checks: "Verificaciones de cumplimiento",
      pass: "Aprobado",
      block: "Bloqueado",
    },
    reports: {
      export: "Exportar reporte",
      csv: "CSV",
      pdf: "PDF (imprimir)",
      methodology: "Metodología y apéndice de cumplimiento",
    },
    compliance: {
      title: "Cumplimiento y consentimiento",
      stopFamily: "Palabras clave STOP / cancelación",
      helpFamily: "Palabras clave HELP",
      quietHours: "Horario de envío",
      disclosureLibrary: "Biblioteca de avisos",
    },
    marketing: {
      nav: {
        about: "Quiénes somos",
        polls: "Tipos de encuestas",
        method: "Metodología",
        research: "Investigación",
        contact: "Contacto",
        launchDemo: "Recorrido por la plataforma",
      },
      hero: {
        eyebrow: "Firma de investigación multilingüe",
        title: "Investigación multilingüe, como tu audiencia realmente responde.",
        subtitle: "Pulso Reach es una plataforma de investigación multilingüe por SMS y WhatsApp, diseñada en torno a cómo las comunidades realmente usan el idioma, la identidad y la confianza. Especialización profunda en español e inglés (donde construimos el método); capacidad creciente en vietnamita, coreano, creole haitiano, árabe y mandarín. Consentimiento, cancelación y horario de envío se cumplen en cada mensaje.",
        ctaPrimary: "Abrir el recorrido",
        ctaSecondary: "Ver tipos de encuestas",
        demoNote: "Pulso Reach es una plataforma activa de investigación multilingüe; el recorrido interactivo corre en tu navegador, así que nada de lo que pulses sale de tu dispositivo.",
      },
      stats: {
        title: "Por qué las comunidades multilingües necesitan otra plataforma",
        items: [
          { value: "~62.5M", label: "población hispana en EE. UU. (cerca del 19% del país y el grupo minoritario más grande); el terreno donde construimos nuestra metodología" },
          { value: "~67M", label: "residentes en EE. UU. que hablan un idioma distinto al inglés en casa: español, chino, tagalo, vietnamita, árabe, coreano, creole francés, entre otros" },
          { value: "~43%", label: "de hispanos en EE. UU. que marcan 'otra raza' en el Censo; señal de cómo los instrumentos estándar aplanan la identidad de muchas comunidades" },
          { value: "~3 de 4", label: "latinos en EE. UU. que dicen no haber oído, o no usar, el término 'Latinx'; la terminología por defecto en la encuestación estándar a menudo no refleja cómo se identifican las personas" },
        ],
      },
      whyDifferent: {
        title: "Encuestar en otros idiomas no es traducir una encuesta en inglés.",
        body: "Décadas de investigación metodológica muestran que las comunidades multilingües en EE. UU. no son una sola audiencia; son capas de grupos de origen nacional, generaciones, perfiles lingüísticos y autoconceptos raciales. Una encuesta traducida genérica, enviada a la hora equivocada, en el dialecto equivocado, por un canal que el participante no confía, sistemáticamente excluirá a las personas cuya voz más necesitas escuchar. La comunidad latina no es un proyecto secundario para nosotros; es donde construimos el método. Ese método ahora sirve a comunidades asiáticas, MENA, caribeñas y africanas inmigrantes con la misma disciplina.",
      },
      pollsHeading: "Encuestas que podemos hacer",
      pollsSub: "Cada tipo de encuesta corre por el mismo motor de cumplimiento (STOP / HELP, horarios, registro de consentimiento, supresión) y permite lógica condicional, preguntas abiertas y cerradas mezcladas, e instrumentos paralelos en varios idiomas (inglés / español bilingüe por defecto; idiomas adicionales bajo pedido).",
      polls: [
        { title: "Intención de voto y seguimiento de boleta", body: "Filtros de votante probable, preferencia de candidato, método de voto (correo / temprano / día de elección) e intención de participar; semanal o rolling tracker." },
        { title: "Saliencia de temas y prueba de políticas", body: "Baterías de temas prioritarios, disyuntivas forzadas y pruebas de encuadre de políticas con cruces por país de origen, idioma y generación." },
        { title: "Persuasión y prueba de mensajes", body: "Brazos de mensajes aleatorizados, scores pre/post, y respuestas abiertas en el idioma preferido del participante." },
        { title: "GOTV y pulsos de participación", body: "Recordatorios cortos y repetibles con seguimiento de tasa de respuesta y auto-reporte de voto, sujetos a horarios y reglas estatales de mensajería política." },
        { title: "Participación cívica y confianza", body: "Confianza en instituciones, discriminación percibida, participación cívica y fuentes de información; diseñadas para que sea seguro responder con honestidad en el idioma propio." },
        { title: "Pulso de empleados y trabajadores de primera línea", body: "Encuestas multilingües para personal por hora, de primera línea y de minorías lingüísticas, donde las encuestas por correo electrónico no funcionan." },
        { title: "Experiencia del cliente y NPS", body: "NPS / CSAT post-interacción para marcas que sirven a consumidores multilingües, con revisión de traducción ítem por ítem y control de estilos de respuesta." },
        { title: "Educación y voz de padres", body: "Encuestas de participación de padres, clima escolar y acceso lingüístico para distritos y organizaciones educativas." },
        { title: "Marca y recordación de anuncios", body: "Recordación espontánea / asistida, sentimiento y prueba de creatividades con cortes por idioma dominante, angloparlantes y bilingües." },
      ],
      method: {
        title: "Cómo hacemos investigación multilingüe (construida sobre nuestro método latino)",
        sub: "Ocho decisiones metodológicas, tomadas de la literatura sobre respuesta de latinos (donde construimos la disciplina), ahora aplicadas en cada comunidad lingüística que servimos.",
        items: [
          { title: "Multilingüe dirigido, no multilingüe a todos", body: "Experimentos recientes muestran que enviar invitaciones bilingües a todos puede reducir levemente la respuesta global, mientras que el multilingüe dirigido a hogares con probabilidad de no ser angloparlantes aumenta el alcance en esas comunidades. Pulso Reach enruta el idioma por preferencia del contacto y señales de apellido / geografía; nunca con un interruptor general." },
          { title: "Identidad multidimensional", body: "Preguntamos país de origen, identidad panétnica, autoconcepto racial, generación y dominio lingüístico como campos separados. La pregunta de raza del Censo de EE. UU. por sí sola no captura cómo se describen la mayoría de los latinos (ni muchos participantes asiáticos, árabes y caribeños)." },
          { title: "'Latinx' (ni etiquetas impuestas) por defecto", body: "La investigación de opinión pública muestra que cerca de tres cuartas partes de los latinos en EE. UU. no han oído o no usan 'Latinx'. Nuestros instrumentos por defecto usan los términos que cada comunidad usa; las etiquetas impuestas sólo aparecen cuando el participante las elige." },
          { title: "BISG + muestreo basado en GIS", body: "Combinamos Bayesian Improved Surname Geocoding con densidad por sector censal (hispana, AAPI, MENA, africana / caribeña negra) para estratificar y ponderar; mejorando el alcance en barrios de idioma dominante y estatus mixto." },
          { title: "Entrevista cognitiva en cada traducción", body: "Los ítems traducidos se prueban cognitivamente con participantes bilingües antes de campo. Incluso traducciones de alta calidad muestran sesgo de ítem; el pretest lo detecta. Esto es innegociable en cualquier idioma en el que trabajemos." },
          { title: "Modo mixto por diseño", body: "SMS primero, con WhatsApp, correo, teléfono y en persona como respaldo para segmentos difíciles de alcanzar. El modo es una covariable, no una constante." },
          { title: "Muestreo dirigido por respondientes para poblaciones ocultas", body: "Para poblaciones indocumentadas, de estatus mixto y otras ocultas usamos cadenas de muestreo dirigido por respondientes con consentimiento más estricto y minimización de datos." },
          { title: "Protocolo cultural: reclutamiento cálido y centrado en la persona", body: "Los guiones de reclutamiento, los mensajes iniciales y los seguimientos cualitativos están diseñados en torno a normas apropiadas para cada comunidad (personalismo, simpatía, pláticas en el trabajo latino; normas equivalentes en otras comunidades); nunca una jerga transaccional de encuesta." },
        ],
      },
      research: {
        title: "Basado en la investigación publicada",
        sub: "La metodología anterior está basada en literatura revisada por pares y documentos de trabajo sobre respuesta de latinos en encuestas (donde la disciplina está más desarrollada), y aplicada a las comunidades lingüísticas que servimos. Fuentes seleccionadas:",
        items: [
          { cite: "Antman, F. M. (2021). Multi-Dimensional Identities of the Hispanic Population in the United States. IZA DP No. 14815.", url: "https://www.iza.org/publications/dp/14815" },
          { cite: "Wagner, J. et al. (2023). Effects of bilingual survey invitations on response in a general-population mail survey.", url: "https://academic.oup.com/jssam" },
          { cite: "Olson, K. et al. (2025). Targeted vs. blanket bilingual outreach in mixed-mode surveys.", url: "https://academic.oup.com/poq" },
          { cite: "Noe-Bustamante, L., Mora, L., y Lopez, M. H. (2020). About one-in-four U.S. Hispanics have heard of Latinx, but just 3% use it. Pew Research Center.", url: "https://www.pewresearch.org/race-and-ethnicity/2020/08/11/about-one-in-four-u-s-hispanics-have-heard-of-latinx-but-just-3-use-it/" },
          { cite: "Barreto, M. A., et al. (2018). Best practices in surveying hard-to-reach Latino populations.", url: "https://www.latinodecisions.com" },
          { cite: "Elliott, M. N., et al. Bayesian Improved Surname Geocoding (BISG) methodology. RAND.", url: "https://www.rand.org/health-care/tools-methods/bisg.html" },
        ],
        disclaimer: "Pulso Reach es una firma de investigación independiente; las fuentes citadas informan nuestra metodología, pero no están afiliadas a nosotros.",
      },
      demoBlock: {
        title: "Ve la plataforma en acción",
        body: "El recorrido interactivo incluye el panel de cumplimiento, el constructor de campañas, los flujos de encuesta con bifurcaciones, el registro de consentimiento, la importación de contactos y los reportes en CSV / PDF imprimible (todo bilingüe inglés / español; idiomas adicionales configurables por proyecto).",
        cta: "Abrir el recorrido",
        features: [
          "Cumplimiento STOP / HELP / START en inglés y español",
          "Bloqueo por horario según zona horaria del contacto",
          "Avisos versionados y registro de consentimiento completo",
          "Editor de flujos con bifurcaciones y flujo de intención de voto incluido",
          "Panel de KPIs en vivo con cortes por idioma y sentimiento",
          "Reportes en CSV e impresión a PDF con apéndice metodológico",
        ],
      },
      contact: {
        title: "Hablemos sobre un estudio",
        body: "Pulso Reach está aceptando nuevos proyectos. Cuéntanos sobre tu audiencia, las preguntas que necesitas responder y tu plazo; te respondemos en un máximo de dos días hábiles.",
        formIntro: "Todos los campos son opcionales excepto nombre y correo; solo usaremos lo que compartas para preparar la respuesta.",
        fields: {
          name: "Nombre",
          email: "Correo",
          org: "Organización",
          topic: "Sobre qué te gustaría hablar",
          message: "Unas líneas sobre tu estudio o pregunta",
          submit: "Enviar mensaje",
          sending: "Enviando\u2026",
          success: "Gracias. Tu mensaje fue recibido; te contactaremos en un máximo de dos días hábiles.",
          error: "Algo falló al enviar el mensaje. Escríbenos directo a contact@pulsoreach.org o intenta de nuevo.",
        },
        topics: [
          "Planear un nuevo estudio",
          "Pregunta de metodología",
          "Solicitud de datos o muestra",
          "Prensa o entrevista",
          "Alianza",
          "Otro",
        ],
        githubFallback: "¿Prefieres GitHub? Visita el repositorio del proyecto.",
        url: "https://github.com/Mdr-palacios/mensajeando",
      },
      footer: {
        rights: "Plataforma activa de investigación multilingüe. Construida con cumplimiento desde el primer día.",
        sourceCode: "Código fuente",
      },
    },
  },
} as const;

export type StringDict = (typeof STRINGS)["en"];
